import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { useApiMutation, useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  Artist,
  ArtistCategory,
  ArtistCategoryListResponse,
  ArtistListResponse,
  CreateArtistRequest,
  FetchArtistsParams,
} from '../types/artistTypes';

const ALL_CATEGORY: ArtistCategory = {
  id: -1,
  category: 'All',
};

// API 호출 함수들
const fetchCategories = async (): Promise<
  ApiResponse<ArtistCategoryListResponse>
> => {
  return api.get<ArtistCategoryListResponse>('/api/v1/artist/category');
};

const fetchArtists = async (
  params: FetchArtistsParams,
): Promise<ApiResponse<ArtistListResponse>> => {
  if (params.keyword && params.keyword.length > 100) {
    throw new Error('Search term must be between 1 and 100 characters');
  }

  const searchParams = new URLSearchParams({
    ...(params.page && { page: params.page.toString() }),
    ...(params.limit && { limit: params.limit.toString() }),
    ...(params.categoryId !== undefined &&
      params.categoryId !== ALL_CATEGORY.id && {
        artistCategoryId: params.categoryId.toString(),
      }),
    ...(params.keyword && { keyword: params.keyword }),
  });

  const url = `/api/v1/artist${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  return api.get<ArtistListResponse>(url);
};

const createArtist = async (
  artistData: CreateArtistRequest,
): Promise<ApiResponse<Artist>> => {
  return api.post<Artist>('/api/v1/artist', artistData);
};

const updateArtist = async (
  artistId: number,
  artistData: CreateArtistRequest,
): Promise<ApiResponse<Artist>> => {
  return api.patch<Artist>(`/api/v1/artist/${artistId}`, artistData);
};

const deleteArtist = async (artistId: number): Promise<ApiResponse<void>> => {
  return api.delete<void>(`/api/v1/artist/${artistId}`);
};

export const useArtist = () => {
  const [categories, setCategories] = useState<ArtistCategory[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<ArtistCategory | null>(null);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [editingArtist, setEditingArtist] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ nameKo: string; nameEn: string }>({
    nameKo: '',
    nameEn: '',
  });
  const [newArtist, setNewArtist] = useState({
    artistCategoryId: '',
    artistNameKO: '',
    artistNameEN: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // 카테고리 목록 조회
  const { data: categoryData, isLoading: categoriesLoading } = useApiQuery(
    ['artist', 'categories'],
    fetchCategories,
  );

  // 아티스트 목록 조회
  const {
    data: artistData,
    isLoading: artistsLoading,
    refetch: refetchArtists,
  } = useApiQuery(
    ['artist', 'list', selectedCategory?.id ?? '', searchTerm],
    () =>
      fetchArtists({
        categoryId: selectedCategory?.id,
        keyword: searchTerm,
        page: 1,
        limit: 100,
      }),
    {
      enabled: !!selectedCategory,
    },
  );

  // 아티스트 생성 mutation
  const createArtistMutation = useApiMutation(createArtist, {
    onSuccess: () => {
      setNewArtist({
        artistCategoryId: selectedCategory?.id.toString() ?? '',
        artistNameKO: '',
        artistNameEN: '',
      });
      refetchArtists();
      alert('Artist created successfully');
    },
    onError: (error: any) => {
      alert(error.message);
    },
  });

  // 아티스트 수정 mutation
  const updateArtistMutation = useApiMutation(
    ({
      artistId,
      artistPayload,
    }: {
      artistId: number;
      artistPayload: CreateArtistRequest;
    }) => updateArtist(artistId, artistPayload),
    {
      onSuccess: () => {
        setEditingArtist(null);
        refetchArtists();
        alert('Artist updated successfully');
      },
      onError: (error: any) => {
        alert(error.message);
      },
    },
  );

  // 아티스트 삭제 mutation
  const deleteArtistMutation = useApiMutation(deleteArtist, {
    onSuccess: () => {
      refetchArtists();
      alert('Artist deleted successfully');
    },
    onError: (error: any) => {
      alert(error.message);
    },
  });

  // 디바운스된 검색 함수
  const debouncedSearch = useCallback(
    debounce((category: number, term: string) => {
      setIsSearching(false);
    }, 500),
    [],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchTerm(value);
      setIsSearching(true);

      if (!selectedCategory) return;

      if (value === '') {
        setIsSearching(false);
        debouncedSearch.cancel();
        return;
      }

      debouncedSearch(selectedCategory.id, value);
    },
    [selectedCategory, debouncedSearch],
  );

  const handleCategoryChange = useCallback((category: ArtistCategory) => {
    setSelectedCategory(category);
    setEditingArtist(null);
    setSearchTerm('');
  }, []);

  const handleEditClick = useCallback((artist: Artist) => {
    setEditingArtist(artist.id);
    setEditForm({
      nameKo: artist.artistNameKO,
      nameEn: artist.artistNameEN,
    });
  }, []);

  const handleSaveEdit = async (artistId: number) => {
    if (!selectedCategory) return;

    if (editForm.nameKo.length > 40) {
      alert('please enter artist name less than 40 characters');
      return;
    }

    if (editForm.nameEn.length > 40) {
      alert('please enter artist name less than 40 characters');
      return;
    }

    updateArtistMutation.mutate({
      artistId,
      artistPayload: {
        artistCategoryId: selectedCategory.id,
        artistNameKO: editForm.nameKo,
        artistNameEN: editForm.nameEn,
      },
    });
  };

  const handleDelete = async (artistId: number) => {
    if (!window.confirm('Are you sure you want to delete this artist?')) return;
    if (!selectedCategory) return;

    deleteArtistMutation.mutate(artistId);
  };

  const handleCreateArtist = async () => {
    if (
      !newArtist.artistCategoryId ||
      !newArtist.artistNameKO ||
      !newArtist.artistNameEN
    ) {
      alert('please fill out all fields');
      return;
    }

    if (newArtist.artistNameKO && newArtist.artistNameKO.length > 40) {
      alert('please enter artist name less than 40 characters');
      return;
    }

    if (newArtist.artistNameEN && newArtist.artistNameEN.length > 40) {
      alert('please enter artist name less than 40 characters');
      return;
    }

    createArtistMutation.mutate({
      artistCategoryId: parseInt(newArtist.artistCategoryId, 10),
      artistNameKO: newArtist.artistNameKO,
      artistNameEN: newArtist.artistNameEN,
    });
  };

  const handleNewArtistChange = useCallback(
    (field: keyof typeof newArtist, value: string) => {
      setNewArtist((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleEditFormChange = useCallback(
    (field: keyof typeof editForm, value: string) => {
      setEditForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  // 카테고리 데이터 처리
  useEffect(() => {
    if (
      categoryData?.success &&
      categoryData.data &&
      categoryData.data.artistCategoryList
    ) {
      const categoriesWithAll = [
        ALL_CATEGORY,
        ...categoryData.data.artistCategoryList,
      ];
      setCategories(categoriesWithAll);

      if (categoriesWithAll.length > 0) {
        setSelectedCategory(categoriesWithAll[0]);
        setNewArtist((prev) => ({
          ...prev,
          artistCategoryId:
            categoryData.data?.artistCategoryList?.[0]?.id?.toString() ?? '',
        }));
      }
    }
  }, [categoryData]);

  // 아티스트 데이터 처리
  useEffect(() => {
    if (artistData?.success && artistData.data && artistData.data.artistList) {
      setArtists(artistData.data.artistList);
    }
  }, [artistData]);

  // cleanup
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    states: {
      categories,
      selectedCategory,
      artists,
      editingArtist,
      editForm,
      newArtist,
      searchTerm,
      isSearching: isSearching || artistsLoading,
      isLoading: categoriesLoading,
    },
    handlers: {
      handleSearch,
      handleCategoryChange,
      handleEditClick,
      handleSaveEdit,
      handleDelete,
      handleCreateArtist,
      handleNewArtistChange,
      handleEditFormChange,
    },
  };
};
